import * as moment from 'moment'
import * as React from 'react'
import { SingleDatePicker } from 'react-dates'
import styled from 'styled-components'
import { createGlobalStyle } from 'styled-components'
import I18n from '../../../../core/i18n'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import { Button } from '../../../atoms'
import { Form } from '../../../molecules'
import { EditTable } from '../../../organisms'
import AdminLayoutIndex from '../_layouts'

declare var window: interfaces.IWindow

interface IProps {
  promotions: any
  csv_path?: any
}

const FIELDS = {
  start_at: 'start_at',
  end_at: 'end_at',
}

const COLUMNS = [
  {
    name: I18n.t('generic.code'),
    field: 'code',
    renderElement: record => <span>{record.promotion.code}</span>,
    required: true,
  },
  {
    name: I18n.t('generic.comment'),
    field: 'comment',
    renderElement: record => <span>{record.promotion.comment}</span>,
    required: false,
  },
  {
    name: I18n.t('generic.total_price'),
    field: 'total_price',
    required: false,
  },
  {
    name: I18n.t('generic.booking_count'),
    field: 'booking_count',
    required: false,
  },
  {
    name: I18n.t('generic.cart_count'),
    field: 'cart_count',
    required: false,
  },
]

const AdminStatisticsReport: React.FC<IProps> = props => {
  Hooks.SetDocumentTitle('Admin statistics report')
  const [promotions, setPromotions] = React.useState(props.promotions)

  const initialParams = new URLSearchParams(window.location.search)
  const [selectedStartDate, setSelectedStartDate] = React.useState(
    initialParams.has('start_at') ? moment(initialParams.get('start_at')) : null
  )
  const [selectedEndDate, setSelectedEndDate] = React.useState(
    initialParams.has('end_at') ? moment(initialParams.get('end_at')) : null
  )

  const [startDateFocused, setStartDateFocused] = React.useState(null)
  const [endDatefocused, setEndDateFocused] = React.useState(null)

  const handleCalendarOutSideRange = React.useCallback(calendarMoment => {
    return false
  }, [])
  const onStartDateChange = React.useCallback((date: moment.Moment | null) => {
    setSelectedStartDate(date)
  }, [])
  const onEndDateChange = React.useCallback((date: moment.Moment | null) => {
    setSelectedEndDate(date)
  }, [])
  const handleSubmit = React.useCallback(
    (_initialValues, _values) => {
      let queryParams = ''

      if (selectedStartDate !== null) {
        queryParams += `?start_at=${selectedStartDate.format('YYYY-MM-DD')}`
      }

      if (selectedEndDate !== null) {
        queryParams += `&end_at=${selectedEndDate.format('YYYY-MM-DD')}`
      }

      location.href = `/admin/promotions/statistics_report${queryParams}`
    },
    [selectedStartDate, selectedEndDate]
  )

  const downloadPath = React.useCallback(() => {
    let queryParams = ''

    if (props.csv_path.indexOf('?') !== -1) {
      queryParams += '&'
    } else {
      queryParams += '?'
    }

    if (selectedStartDate !== null) {
      queryParams += `start_at=${selectedStartDate.format('YYYY-MM-DD')}`
    }

    if (selectedEndDate !== null) {
      queryParams += `&end_at=${selectedEndDate.format('YYYY-MM-DD')}`
    }

    return props.csv_path + queryParams
  }, [selectedStartDate, selectedEndDate])

  return (
    <AdminLayoutIndex
      model="promotion"
      title={
        <Heading>
          <span>Promotions Statistics Report</span>
          <br />
          <Form fields={FIELDS} handleSubmit={handleSubmit}>
            <div className="SearchInputs">
              <div className="SearchPanel_FormItem">
                <DateRangePickerCustomStyle />
                <SingleDatePicker
                  id="start_at"
                  date={selectedStartDate}
                  placeholder={I18n.t('generic.start_at')}
                  onDateChange={onStartDateChange}
                  focused={startDateFocused}
                  onFocusChange={changed => setStartDateFocused(changed.focused)}
                  enableOutsideDays={true}
                  isOutsideRange={handleCalendarOutSideRange}
                  numberOfMonths={1}
                  hideKeyboardShortcutsPanel={true}
                />
              </div>
              <div className="SearchPanel_FormItem">
                <DateRangePickerCustomStyle />
                <SingleDatePicker
                  id="end_at"
                  date={selectedEndDate}
                  placeholder={I18n.t('generic.end_at')}
                  onDateChange={onEndDateChange}
                  focused={endDatefocused}
                  onFocusChange={changed => setEndDateFocused(changed.focused)}
                  enableOutsideDays={true}
                  isOutsideRange={handleCalendarOutSideRange}
                  numberOfMonths={1}
                  hideKeyboardShortcutsPanel={true}
                />
              </div>
              <br />
              <Button primary={true}>Search</Button>
            </div>
          </Form>
          <br />
          {props.csv_path && (
            <Button small={true}>
              <a href={downloadPath()}>{I18n.t('generic.csv') + I18n.t('generic.download')}</a>
            </Button>
          )}
        </Heading>
      }
      main={<EditTable editable={false} columns={COLUMNS} records={promotions} />}
    />
  )
}

const Heading = styled.div`
  .Button {
    margin-left: 12px;
  }
`

const DateRangePickerCustomStyle = createGlobalStyle`
  .SingleDatePicker {
    display: block;
  }

  .DateRangePicker.DateRangePicker_1 {
    width: 100%;
  }

  .DateRangePickerInput.DateRangePickerInput_1 {
    width: 100%;
    border: none;
  }

  .DateRangePickerInput_arrow.DateRangePickerInput_arrow_1 {
    display: none;
  }

  .SingleDatePickerInput__withBorder {
    width: 100%;
    border: solid 1px ${constants.COLORS.divider};
    border-left: none;
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      border: solid 1px ${constants.COLORS.divider};
      border-radius: 3px;
    }
  }

  .CalendarDay__selected_span {
    background: ${constants.COLORS.secondary};
    border: 1px double ${constants.COLORS.secondary};
    color: ${constants.COLORS.white};
  }

  .CalendarDay__selected {
    background: ${constants.COLORS.primary};
    border: 1px double ${constants.COLORS.primary};
    color: ${constants.COLORS.white};
  }

  .CalendarDay__selected:hover {
    background: ${constants.COLORS.primary};
    color: ${constants.COLORS.white};
  }

  .CalendarDay__hovered_span:hover,
  .CalendarDay__hovered_span {
    background: ${constants.COLORS.secondary};
    border: 1px double ${constants.COLORS.secondary};
    color: ${constants.COLORS.white};
  }
`

export default AdminStatisticsReport
